// Important: we are using `any` here to improve type performance with Mui theming.
// Read more: https://github.com/tjx666/ts-perf-issue?tab=readme-ov-file#createtheme

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { useMemo, type ReactNode } from "react";

import type { DesignerTheme } from "./DesignerTheme";
import * as themes from "./themes";
import type { DesignerThemeNames } from "./types";

interface ThemeProviderProps {
  themeName: DesignerThemeNames;
  children: ReactNode;
}

// Pre-compute themes at module level to avoid expensive type comparisons
const themeCache = new Map(Object.values(themes).map((theme) => [theme.name, theme]));

const createMuiTheme = (theme: DesignerTheme<any>) => {
  const { materialThemeOptions, ...designerThemeOptions } = theme;
  return (createTheme as unknown as (baseTheme: any) => any)({ ...materialThemeOptions, ...designerThemeOptions });
};

const getTheme = (themeName: DesignerThemeNames) => {
  const theme = themeCache.get(themeName);

  if (!theme) throw new Error(`Unable to find theme named "${themeName}"?`);

  return theme;
};

export const DesignerThemeProvider = ({ themeName, children }: ThemeProviderProps) => {
  const theme = useMemo(() => getTheme(themeName), [themeName]);
  const muiTheme = createMuiTheme(theme as any);

  return (
    <EmotionThemeProvider theme={theme as any}>
      <MuiThemeProvider theme={muiTheme as any}>{children}</MuiThemeProvider>
    </EmotionThemeProvider>
  );
};
